import { Button, makeStyles, mergeClasses, tokens } from '@fluentui/react-components';
import {
  Grid20Regular,
  Laptop20Regular,
  Layer20Regular,
  Organization20Regular,
  Pulse20Regular,
  Router20Regular,
  ShieldKeyhole20Regular,
} from '@fluentui/react-icons';
import { useState, type ReactNode } from 'react';
import { Link, NavLink, Outlet } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { useMe } from '../auth/MeProvider';
import { networksFromDevices } from '../lib/networks';
import { ShellProvider, useShell } from '../lib/shell';
import { costView } from '../pages/overview/model';
import { vt } from '../theme';
import { PageAsideHost } from './PageAside';
import { StatusBar } from './StatusBar';

const SIDEBAR = 248;

const useStyles = makeStyles({
  root: {
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
    color: vt.fg1,
    // The ambient wash: cool blue high-left, violet low-right, on a near-flat ground.
    backgroundColor: vt.canvas,
    backgroundImage: `radial-gradient(circle 420px at 144px 108px, ${vt.washA}, transparent), radial-gradient(circle 480px at calc(100% - 115px) calc(100% - 90px), ${vt.washB}, transparent)`,
    fontSize: '12.5px',
  },
  body: { flexGrow: 1, display: 'flex', minHeight: 0 },
  sidebar: {
    width: `${SIDEBAR}px`,
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box',
    paddingBottom: '17px',
    overflowY: 'auto',
  },
  lockup: {
    display: 'flex',
    alignItems: 'center',
    columnGap: '13px',
    padding: '16px 18px 0',
    height: '43px',
    boxSizing: 'border-box',
    marginBottom: '15px',
    color: vt.fg1,
  },
  mark: { width: '26px', height: '22.5px', flexShrink: 0, fill: 'currentColor' },
  wordmark: { display: 'flex', flexDirection: 'column', lineHeight: 1.1 },
  eyebrow: { fontSize: '7.5px', letterSpacing: '1.4px', fontWeight: tokens.fontWeightSemibold, color: vt.fg3 },
  product: { fontSize: '13px', fontWeight: tokens.fontWeightSemibold, letterSpacing: '-0.1px' },
  nav: { display: 'flex', flexDirection: 'column' },
  navItem: {
    display: 'flex',
    alignItems: 'center',
    columnGap: '10px',
    height: '30px',
    marginLeft: '8px',
    width: `${SIDEBAR - 24}px`,
    boxSizing: 'border-box',
    paddingInline: '12px 6px',
    borderRadius: '8px',
    color: vt.fg2,
    textDecorationLine: 'none',
    fontSize: '12.5px',
    ':hover': { backgroundColor: vt.tintNeutral },
    ':focus-visible': { outline: `2px solid ${vt.brand}`, outlineOffset: '-2px' },
  },
  navActive: {
    backgroundColor: vt.brandTint,
    color: vt.fg1,
    fontWeight: tokens.fontWeightSemibold,
    ':hover': { backgroundColor: vt.brandTint },
  },
  navIcon: { color: vt.fg3, display: 'flex', '& svg': { width: '16px', height: '16px' } },
  navIconActive: { color: vt.brand },
  navLabel: { flexGrow: 1 },
  count: { fontFamily: tokens.fontFamilyMonospace, fontSize: '10px', color: vt.fg4, fontWeight: tokens.fontWeightRegular },
  attention: {
    minWidth: '18px',
    height: '16px',
    boxSizing: 'border-box',
    paddingInline: '6px',
    marginRight: '2px',
    borderRadius: tokens.borderRadiusCircular,
    backgroundColor: vt.orange,
    color: vt.onWarm,
    fontSize: '10px',
    fontWeight: tokens.fontWeightBold,
    lineHeight: '16px',
    textAlign: 'center',
  },
  section: { marginTop: '18px', display: 'flex', flexDirection: 'column' },
  sectionLabel: {
    paddingLeft: '20px',
    height: '22px',
    lineHeight: '22px',
    fontSize: '11px',
    fontWeight: tokens.fontWeightSemibold,
    color: vt.fg3,
  },
  netList: { listStyleType: 'none', margin: 0, padding: 0 },
  netRow: {
    display: 'flex',
    alignItems: 'center',
    columnGap: '9px',
    height: '25px',
    paddingInline: '27px 22px',
    color: vt.fg2,
  },
  netLink: {
    textDecorationLine: 'none',
    borderRadius: '8px',
    marginInline: '8px',
    paddingInline: '19px 14px',
    ':hover': { backgroundColor: vt.tintNeutral, color: vt.fg1 },
    ':focus-visible': { outline: `2px solid ${vt.brand}`, outlineOffset: '-2px' },
  },
  netDot: { width: '6.4px', height: '6.4px', borderRadius: '50%', backgroundColor: vt.fg3, flexShrink: 0 },
  netName: { flexGrow: 1 },
  spacer: { flexGrow: 1 },
  foot: {
    display: 'flex',
    alignItems: 'center',
    columnGap: '8px',
    paddingInline: '20px 16px',
    minHeight: '30px',
  },
  user: { flexGrow: 1, color: vt.fg2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  main: {
    flexGrow: 1,
    minWidth: 0,
    margin: '10px',
    padding: '28px',
    boxSizing: 'border-box',
    borderRadius: '12px',
    backgroundColor: vt.pane,
    boxShadow: `0 1px 2px ${vt.paneShadow}, 0 8px 24px ${vt.paneShadow}`,
    overflow: 'auto',
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalXL,
  },
  // Empty unless a page renders a <PageAside>; then it holds that pane beside the main one.
  asideHost: { display: 'flex', flexShrink: 0, minHeight: 0 },
});

/** The VIPPER mark (vpn repo docs/mockups/lib/logo.mjs markPath, side 5 lattice units). */
function Mark({ className }: { className: string }) {
  return (
    <svg className={className} viewBox="0 0 5 4.3301" aria-hidden>
      <path d="M 0 0 L 5 0 L 2.5 4.3301 L 1 1.7321 L 2 1.7321 L 2.5 2.5981 L 3.5 0.866 L 0.5 0.866 Z" />
    </svg>
  );
}

function NavItem({ to, icon, label, badge }: { to: string; icon: ReactNode; label: string; badge?: ReactNode }) {
  const s = useStyles();
  return (
    <NavLink to={to} end={to === '/'} className={({ isActive }) => mergeClasses(s.navItem, isActive && s.navActive)}>
      {({ isActive }) => (
        <>
          <span className={mergeClasses(s.navIcon, isActive && s.navIconActive)}>{icon}</span>
          <span className={s.navLabel}>{label}</span>
          {badge}
        </>
      )}
    </NavLink>
  );
}

function NetRow({ id, cidr }: { id: string; cidr: string }) {
  const s = useStyles();
  return (
    <>
      <span className={s.netDot} aria-hidden />
      <span className={s.netName}>{id}</span>
      <span className={s.count}>/{cidr.split('/')[1]}</span>
    </>
  );
}

function Shell() {
  const s = useStyles();
  const { user, signOut } = useAuth();
  const { me, isAdmin } = useMe();
  const { relay, devices, networks: networkList, changeCount, cost, costError } = useShell();
  const name = me?.name ?? (user?.profile.name as string | undefined) ?? user?.profile.preferred_username ?? user?.profile.sub;
  // Admins get GET /networks (empty networks too, and their real prefix); others derive theirs from /devices.
  const networks = networkList
    ? networkList.map((n) => ({ id: n.id, cidr: n.cidr, devices: n.deviceCount }))
    : networksFromDevices(devices ?? []);
  const [aside, setAside] = useState<HTMLElement | null>(null);

  return (
    <div className={s.root}>
      <div className={s.body}>
        <aside className={s.sidebar}>
          <div className={s.lockup}>
            <Mark className={s.mark} />
            <span className={s.wordmark}>
              <span className={s.eyebrow}>NETWORK</span>
              <span className={s.product}>Infrastructure</span>
            </span>
          </div>

          <nav className={s.nav} aria-label="Sections">
            <NavItem to="/" icon={<Grid20Regular />} label="Overview" />
            {isAdmin && <NavItem to="/map" icon={<Organization20Regular />} label="Network map" />}
            <NavItem
              to="/devices"
              icon={<Laptop20Regular />}
              label="Devices"
              badge={devices && <span className={s.count}>{devices.length}</span>}
            />
            {isAdmin && <NavItem to="/networks" icon={<Layer20Regular />} label="Networks" />}
            {isAdmin && (
              <NavItem
                to="/firewall"
                icon={<ShieldKeyhole20Regular />}
                label="Firewall"
                badge={
                  !!changeCount && (
                    <span className={s.attention} aria-label={`${changeCount} unpublished changes`}>
                      {changeCount}
                    </span>
                  )
                }
              />
            )}
            {isAdmin && <NavItem to="/traffic" icon={<Pulse20Regular />} label="Traffic" />}
            {isAdmin && <NavItem to="/sites" icon={<Router20Regular />} label="Sites" />}
          </nav>

          {networks.length > 0 && (
            <section className={s.section} aria-label="Networks">
              <div className={s.sectionLabel}>Networks</div>
              <ul className={s.netList}>
                {networks.map((n) => (
                  <li key={n.id} title={`${n.cidr} · ${n.devices} device${n.devices === 1 ? '' : 's'}`}>
                    {/* Quick links into the Networks page for admins; plain rows for everyone else. */}
                    {isAdmin ? (
                      <Link to={`/networks?n=${encodeURIComponent(n.id)}`} className={mergeClasses(s.netRow, s.netLink)}>
                        <NetRow id={n.id} cidr={n.cidr} />
                      </Link>
                    ) : (
                      <span className={s.netRow}>
                        <NetRow id={n.id} cidr={n.cidr} />
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          )}

          <span className={s.spacer} />
          <div className={s.foot}>
            {name && <span className={s.user}>{name}</span>}
            <Button appearance="subtle" size="small" shape="circular" onClick={() => void signOut()}>
              Sign out
            </Button>
          </div>
        </aside>
        <PageAsideHost.Provider value={aside}>
          <main className={s.main}>
            <Outlet />
          </main>
        </PageAsideHost.Provider>
        <div ref={setAside} className={s.asideHost} />
      </div>
      <StatusBar relay={relay.relay} cost={costView(cost, costError, isAdmin || !me)} />
    </div>
  );
}

export function Layout() {
  return (
    <ShellProvider>
      <Shell />
    </ShellProvider>
  );
}
